import { Stack } from '@mui/material'
import MoveRequestShelvePosition from './MoveRequestShelvePosition'
import { useEffect, useReducer, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import useToggle from './../../../generic/hooks/useToggle/useToggle'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../../../generic/smartSelect'
import { CompassButton } from '../../../Compass/Common/CompassButton'
import { useLocation } from 'react-router-dom'

const MoveRequest = (props) => {
  const { moveModeByLocation } = props

  const [AlertElement, showAlert] = AlertUI()

  const [devicesFrom, setDevicesFrom] = useState([])
  const [devicesTo, setDevicesTo] = useState([])
  const [freePositions, setFreePositions] = useState([])
  const [nonFreePositions, setNonFreePositions] = useState([])
  const [resourcesOnCell, setResourcesOnCell] = useState([])
  const [toggle, changeToggle] = useToggle()
  const [firstLoad, setFirstLoad] = useState(true)
  const { t_ } = useSmartTranslation()

  const types = [
    { label: t_('Fixture'), value: 'Fixture' },
    { label: t_('Part'), value: 'Part' },
    { label: t_('Raw Material'), value: 'Raw Material' },
    { label: t_('Tool'), value: 'Tool' }
  ]

  const reducerAction = (state, action) => {
    if (action.type === 'setFromDevice') {
      changeToggle() // Toggle to refresh data
      return { ...state, fromDevice: devicesFrom.find(device => device.name === action.value), fromPosition: null }
    }
    if (action.type === 'setToDevice') {
      return { ...state, toDevice: devicesTo.find(device => device.name === action.value), toPosition: null }
    }
    if (action.type === 'showErrors') {
      return { ...state, showErrors: true }
    }
    if (action.type === 'setShelf') {
      if (action.mode === 'from') {
        return { ...state, fromPosition: { shelf: action.value, row: null, column: null } }
      } else {
        if (!action.value) return { ...state }
        return { ...state, toPosition: { shelf: action.value, row: null, column: null } }
      }
    }
    if (action.type === 'setRow') {
      if (action.mode === 'from') {
        return { ...state, fromPosition: { ...state.fromPosition, row: action.value, column: null } }
      } else {
        if (!action.value) return { ...state }
        return { ...state, toPosition: { ...state.toPosition, row: action.value, column: null } }
      }
    }
    if (action.type === 'setColumn') {
      if (action.mode === 'from') {
        if (resourcesOnCell === undefined || resourcesOnCell.length === 0) return { ...state }
        const currentPositionFixture = nonFreePositions.find(pos => pos.shelve === state.fromPosition.shelf && pos.row === state.fromPosition.row && pos.column === action.value)
        if (!currentPositionFixture) return { ...state }
        const currentResource = resourcesOnCell.find(res => res.fixtureId === currentPositionFixture.currentFixture)
        return { ...state, fromPosition: { ...state.fromPosition, column: action.value, resource: currentResource } }
      } else {
        if (!action.value) return { ...state }
        return { ...state, toPosition: { ...state.toPosition, column: action.value } }
      }
    }
    if (action.type === 'setType') {
      changeToggle() // Toggle to refresh data
      return { ...state, resourceType: action.value, resourceReference: null, resource: null }
    }
    if (action.type === 'setResourceReference') {
      return { ...state, resourceReference: action.value, resource: null }
    }
    if (action.type === 'setResource') {
      return { ...state, resource: action.value }
    }
    if (action.type === 'init') {
      return { fromDevice: null, toDevice: null, showErrors: false, fromPosition: null, toPosition: null, resourceType: null, resourceReference: null, resource: null }
    }
    return state
  }
  const [selectedData, dispatchSelectedData] = useReducer(reducerAction, { fromDevice: null, toDevice: null, showErrors: false, fromPosition: null, toPosition: null, resourceType: null, resourceReference: null, resource: null })
  const location = useLocation()
  const { rowFirstResource, position, device } = location.state || {}
  const botonLabel = location.state ? t_('Accept') : t_('Request Move')
  const selectedResourceType = selectedData?.resourceType?.value ? selectedData.resourceType?.value : rowFirstResource?.type.name
  const selectedResourceReference = selectedData?.resourceReference ? selectedData.resourceReference : rowFirstResource?.reference.code
  const selectedResourceCode = selectedData?.resource?.code ? selectedData.resource?.code : rowFirstResource?.code
  if (rowFirstResource) {
    const resource = resourcesOnCell.find((item) =>
      item.type === selectedResourceType &&
      item.reference === selectedResourceReference &&
      item.code === selectedResourceCode
    )
    if (resource) {
      selectedData.resource = resource
    }
  }

  useEffect(() => {
    if (!devicesFrom || devicesFrom.length === 0) {
      return
    }

    if (!firstLoad) return

    if (device && position) {
      dispatchSelectedData({ type: 'setFromDevice', value: device.name })
      dispatchSelectedData({ type: 'setShelf', value: position.shelf, mode: 'from' })
      dispatchSelectedData({ type: 'setRow', value: position.row, mode: 'from' })
      dispatchSelectedData({ type: 'setColumn', value: position.column, mode: 'from' })
      setFirstLoad(false)
    }
  }, [device, position, devicesFrom, firstLoad])

  // useEffect to load positions after device from is selected
  const loadPositionsForDevice = useCallback(async (device, mode, load) => {
    if (!device || !device.hasPositions) {
      dispatchSelectedData({ type: 'setShelf', value: null, mode })
      dispatchSelectedData({ type: 'setRow', value: null, mode })
      dispatchSelectedData({ type: 'setColumn', value: null, mode })
      return
    }

    const positions = await apiGet('commandMove/' + device.id + '/positions' + (mode === 'from' ? '/false' : '')).catch(() => [])
    if (positions.length === 1 && load) {
      // If there is only 1 position, autocomplete them
      const position = positions[0]
      dispatchSelectedData({ type: 'setShelf', value: position.shelve, mode })
      dispatchSelectedData({ type: 'setRow', value: position.row, mode })
      dispatchSelectedData({ type: 'setColumn', value: position.column, mode })
    }
  }, [])
  useEffect(() => {
    loadPositionsForDevice(selectedData?.fromDevice, 'from', true)
  }, [selectedData?.fromDevice, loadPositionsForDevice])
  useEffect(() => {
    loadPositionsForDevice(selectedData?.toDevice, 'to', false)
  }, [selectedData?.toDevice, loadPositionsForDevice])
  useEffect(() => {
    const loadResources = async () => {
      setResourcesOnCell(await apiGet('commandMove/resourcesInsideCell'))
    }
    loadResources()
  }, [moveModeByLocation, toggle])

  useEffect(() => {
    const loadPositions = async () => {
      const data = await apiGet('commandMove/' + selectedData?.fromDevice?.id + '/positions/false').catch(_ => [])
      setNonFreePositions(data.sort((a, b) => a.shelve - b.shelve || a.row - b.row || a.column - b.column))
    }

    if (!selectedData.fromDevice || !selectedData?.fromDevice?.hasPositions) {
      setNonFreePositions([])
      return
    }
    loadPositions()
  }, [selectedData?.fromDevice, position])

  useEffect(() => {
    const loadPositions = async () => {
      const data = await apiGet('commandMove/' + selectedData.toDevice.id + '/positions')
      setFreePositions(data.sort((a, b) => a.shelve - b.shelve || a.row - b.row || a.column - b.column))
    }

    if (!selectedData.toDevice || !selectedData.toDevice.hasPositions) {
      setFreePositions([])
      return
    }
    loadPositions()
  }, [selectedData?.toDevice])

  useEffect(() => {
    const loadDevices = async () => {
      const devices = await apiGet('commandMove/devices')
      setDevicesFrom(devices.filter(device => device.hasResource))
      setDevicesTo(devices.filter(device => !device.deviceIsFull))
    }
    loadDevices()

    const intervalId = setInterval(() => {
      loadDevices()
    }, 1000)

    // Clean up interval on component unmount
    return () => clearInterval(intervalId)
  }, [])

  const move = async () => {
    let fromPosition = null
    let toPosition = null

    if (!selectedData.toDevice) {
      dispatchSelectedData({ type: 'showErrors' })
      return
    }

    if (selectedData.toPosition !== null && selectedData.toDevice.shelf !== null && selectedData.toDevice.row !== null && selectedData.toDevice.col !== null) {
      toPosition = freePositions.find((item) => item.shelve === selectedData.toPosition.shelf && item.row === selectedData.toPosition.row && item.column === selectedData.toPosition.column)?.id
      if (!toPosition) {
        dispatchSelectedData({ type: 'showErrors' })
        return
      }
    } else {
      toPosition = null
    }
    if (moveModeByLocation) {
      if (!selectedData.fromDevice) {
        dispatchSelectedData({ type: 'showErrors' })
        return
      }
      if (selectedData?.fromDevice?.hasPositions) {
        if ((!selectedData.fromPosition) || (!selectedData?.fromPosition?.shelf) || (!selectedData?.fromPosition?.row) || (!selectedData?.fromPosition?.column)) {
          dispatchSelectedData({ type: 'showErrors' })
          return
        }
        fromPosition = nonFreePositions.find((item) =>
          parseInt(item.shelve) === parseInt(selectedData?.fromPosition?.shelf) &&
          parseInt(item.row) === parseInt(selectedData?.fromPosition?.row) &&
          parseInt(item.column) === parseInt(selectedData?.fromPosition?.column)).id
      }
    } else {
      if (!selectedData.resource) {
        dispatchSelectedData({ type: 'showErrors' })
        return
      }
    }

    const fromDevice = { deviceId: selectedData?.fromDevice?.id ? selectedData?.fromDevice?.id : device?.id, positionId: fromPosition }
    const toDevice = { deviceId: selectedData.toDevice.id, positionId: toPosition }
    let url = 'commandMove/request'
    if (selectedData.resource?.fixtureId !== undefined) {
      url = 'commandMove/request/fixture/' + selectedData.resource?.fixtureId
    }

    const data = [
      // Must be this order to work with the backend
      // 0 -> fromDevice, 1 -> toDevice
      fromDevice,
      toDevice
    ]

    try {
      await apiPost(url, data)
      showAlert({
        title: '',
        message: t_('Move request done'),
        severity: 'success'
      })
      changeToggle()
      dispatchSelectedData({ type: 'init' })
    } catch (error) {
      showAlert({
        title: '',
        message: error.message,
        severity: 'error'
      })
    }
  }

  const references = Array.from(new Set(resourcesOnCell.filter(resource => selectedData.resourceType && resource.type === selectedData.resourceType.value).map(item => item.reference)))

  const codes = Array.from(new Set(resourcesOnCell.filter(resource => selectedData.resourceType && resource.type === selectedData.resourceType.value && selectedData.resourceReference && selectedData.resourceReference === resource.reference).map(item => item.code)))
  return (
    <Stack justifyContent='space-around' direction='row'>
      <Stack direction='column' alignItems='flex-start'>
        <h2>{moveModeByLocation ? t_('Move by location') : t_('Move by resource')}</h2>
        <div style={{ marginBottom: '1rem' }}>
          {AlertElement}
        </div>
        <Stack direction='row'>
          <Stack direction='row' gap={10}>
            <Stack gap={3}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto', width: moveModeByLocation ? 'fit-content' : 'initial', alignItems: 'center', gap: '3rem 6rem', justifyContent: 'space-between' }}>
                {!moveModeByLocation
                  ? (
                    <>
                      <div style={{ fontSize: '1.3rem' }}>
                        {moveModeByLocation ? t_('From') : t_('Resource')}
                      </div>
                      <SmartSelect
                        value={selectedResourceType}
                        selectableOptions={types.map(item => item.value?.toString())}
                        label={t_('Type')}
                        onChange={(newValue) => {
                          dispatchSelectedData({ type: 'setType', value: types.find((item) => item.value === newValue) })
                        }}
                        minWidth='17rem'
                        error={selectedData.showErrors && !selectedResourceType}
                        fontSize='1.2rem'
                      />
                      <Stack direction='row' gap={6} alignItems='center'>
                        <SmartSelect
                          value={selectedResourceReference}
                          selectableOptions={references}
                          onChange={(value) => {
                            dispatchSelectedData({ type: 'setResourceReference', value })
                          }}
                          minWidth='17rem'
                          disabled={!selectedResourceType}
                          label={t_('Reference')}
                          error={selectedData.showErrors && selectedResourceType && !selectedResourceReference}
                          fontSize='1.2rem'
                        />
                        <SmartSelect
                          value={selectedResourceCode}
                          selectableOptions={codes}
                          onChange={(code) => {
                            const resource = resourcesOnCell.find((item) =>
                              item.type === selectedResourceType &&
                              item.reference === selectedResourceReference &&
                              item.code === code
                            )
                            dispatchSelectedData({ type: 'setResource', value: resource })
                          }}
                          minWidth='14rem'
                          disabled={!selectedResourceReference}
                          label={t_('Code')}
                          error={selectedData.showErrors && selectedResourceType && selectedResourceReference && !selectedResourceCode}
                          fontSize='1.2rem'
                        />
                      </Stack>
                    </>)
                  : null}
                {moveModeByLocation
                  ? (
                    <>
                      <div style={{ fontSize: '1.3rem' }}>{t_('From')}</div>
                      <SmartSelect
                        value={selectedData.fromDevice?.name}
                        selectableOptions={devicesFrom.map((item) => item.name)}
                        onChange={(item) => { dispatchSelectedData({ type: 'setFromDevice', value: item }) }}
                        minWidth='17rem'
                        label={t_('Asset')}
                        disableClearable={false}
                        autoSelectFirst={false}
                        error={selectedData.showErrors && !selectedData.fromDevice}
                        fontSize='1.2rem'
                      />
                      <MoveRequestShelvePosition
                        positions={nonFreePositions}
                        position={{ ...selectedData.fromPosition, mode: 'from' }}
                        disabled={(!selectedData.fromDevice)}
                        onChange={(action) => { dispatchSelectedData({ ...action, mode: 'from' }) }}
                        showErrors={selectedData.showErrors}
                      />
                    </>)
                  : null}
                <div style={{ fontSize: '1.3rem' }}>{t_('To')}</div>
                <SmartSelect
                  value={selectedData.toDevice?.name}
                  selectableOptions={devicesTo.map((item) => item.name)}
                  onChange={(item) => { dispatchSelectedData({ type: 'setToDevice', value: item }) }}
                  disableClearable={false}
                  autoSelectFirst={false}
                  error={selectedData.showErrors && !selectedData.toDevice}
                  label={t_('Asset')}
                  fontSize='1.2rem'
                />
                <MoveRequestShelvePosition
                  positions={freePositions}
                  position={{ ...selectedData.toPosition, mode: 'to' }}
                  selectedResource={moveModeByLocation ? selectedData.fromPosition?.resource : selectedData.resource}
                  disabled={!selectedData.toDevice || !selectedData.toDevice.hasPositions}
                  onChange={(action) => {
                    dispatchSelectedData({ ...action, mode: 'to' })
                  }}
                />
              </div>
            </Stack>
            <CompassButton labelStyle={{ fontSize: '1.8rem' }} onClick={move} label={botonLabel} width='auto' sx={{ paddingLeft: '2rem', paddingRight: '2rem' }} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default MoveRequest
